import { z } from 'zod';

// Setter spec §06 (motor de prioridades), §02 (llamada entrante = máxima prioridad), §05.22 (transferencia inmediata),
// C9 (entrantes fuera de horario). Wave 4B. Everything provider-facing (Telnyx transfer, real concurrency of the voice
// service) is fake-tested: «sin verificar».

const Id = z.string().min(1);
const Iso = z.string();

/**
 * Explicit priority classes for work that competes for AI call capacity, highest first (spec §06.1–§06.8). WhatsApp
 * replies (§06.3) never wait for call capacity: they run in parallel and are not in this queue.
 *  • inbound_live            §06.2 — an inbound call is answered before any outbound work.
 *  • committed_time          §06.1/§06.9 — a time the prospect agreed («llámame a las 4»).
 *  • team_request            a call a person of the team asked for right now (not in the spec's list; placed just below
 *                            committed times — owner may move it).
 *  • imminent_reminder       §06.4 — confirmation/reminder of an upcoming appointment.
 *  • no_show_recovery        §06.5 — after a human-confirmed no-show.
 *  • new_lead                §06.8/§02 — first attempt of a lead that just arrived (SLA < 5 min).
 *  • night_lead              §06.8 — first attempt of a lead that arrived during the night, oldest first at 08:00.
 *  • qualified_no_appointment §06.6 — qualified, still without an appointment.
 *  • window_expiring         §06.7 — WhatsApp window about to expire (contextual follow-up only).
 *  • routine_cadence         the rest of the approved cadences.
 */
export const PRIORITY_CLASSES = [
  'inbound_live',
  'committed_time',
  'team_request',
  'imminent_reminder',
  'no_show_recovery',
  'new_lead',
  'night_lead',
  'qualified_no_appointment',
  'window_expiring',
  'routine_cadence',
] as const;
export const PriorityClassSchema = z.enum(PRIORITY_CLASSES);
export type PriorityClass = z.infer<typeof PriorityClassSchema>;

/** Rank = position in PRIORITY_CLASSES (0 = highest). */
export const priorityRank = (c: PriorityClass): number => PRIORITY_CLASSES.indexOf(c);

export const PRIORITY_CLASS_LABELS: Record<PriorityClass, string> = {
  inbound_live: 'Llamada entrante en vivo',
  committed_time: 'Hora pactada con el prospecto',
  team_request: 'Solicitada por el equipo',
  imminent_reminder: 'Recordatorio de cita próxima',
  no_show_recovery: 'Recuperación de no show',
  new_lead: 'Lead nuevo (SLA < 5 min)',
  night_lead: 'Lead nocturno (más antiguo primero)',
  qualified_no_appointment: 'Calificado sin cita',
  window_expiring: 'Ventana de WhatsApp por vencer',
  routine_cadence: 'Cadencia de rutina',
};

export const PRIORITY_CLASS_SPEC_REFS: Record<PriorityClass, string> = {
  inbound_live: '§06.2',
  committed_time: '§06.1 · §06.9',
  team_request: '—',
  imminent_reminder: '§06.4',
  no_show_recovery: '§06.5',
  new_lead: '§06.8 · §02',
  night_lead: '§06.8',
  qualified_no_appointment: '§06.6',
  window_expiring: '§06.7',
  routine_cadence: '§03',
};

/** Defaults: the spec's SLA (5 min) and a short capacity re-check; concurrency default comes from server config. */
export const PRIORITY_DEFAULTS = { newLeadSlaMinutes: 5, recheckSeconds: 20 } as const;

export const WorkspacePriorityConfigSchema = z
  .object({
    /** Simultaneous AI calls for the whole workspace (inbound + outbound). null = server default (VOICE_MAX_CONCURRENT_CALLS). */
    maxConcurrentCalls: z.number().int().min(1).max(500).nullable(),
    /** Optional per-line limit (endpoint id → simultaneous AI calls on that line). */
    lineLimits: z.record(z.string().min(1).max(64), z.number().int().min(1).max(500)).default({}),
    /** How often a call waiting for capacity asks again (seconds). */
    recheckSeconds: z.number().int().min(5).max(600).default(PRIORITY_DEFAULTS.recheckSeconds),
    /** First-attempt SLA for new form leads (spec: < 5 min). */
    newLeadSlaMinutes: z.number().int().min(1).max(120).default(PRIORITY_DEFAULTS.newLeadSlaMinutes),
  })
  .strict();
export type WorkspacePriorityConfig = z.infer<typeof WorkspacePriorityConfigSchema>;

export const ProjectPriorityConfigSchema = z
  .object({
    /**
     * C9 (spec §02, §06.2): inbound calls out of the AI's hours are answered by the AI. Night rules only forbid
     * ORIGINATING calls. Spec default ON; off = rejected + callback task, as before.
     */
    inboundAiOutOfHours: z.boolean().default(true),
  })
  .strict();
export type ProjectPriorityConfig = z.infer<typeof ProjectPriorityConfigSchema>;

export const PutWorkspacePrioritySettingsSchema = WorkspacePriorityConfigSchema;
export const PutProjectPrioritySettingsSchema = ProjectPriorityConfigSchema;

export const ProjectPrioritySettingsSchema = z.object({
  projectId: Id,
  config: ProjectPriorityConfigSchema,
  /** true = nothing saved yet (spec defaults). */
  isDefault: z.boolean(),
  note: z.string(),
});
export type ProjectPrioritySettings = z.infer<typeof ProjectPrioritySettingsSchema>;

export const QueueItemStateSchema = z.enum(['waiting', 'admitted', 'dropped']);
export const PriorityQueueItemSchema = z.object({
  id: Id,
  callId: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  class: PriorityClassSchema,
  state: QueueItemStateSchema,
  /** Position among waiting items (1 = next), null when not waiting. */
  position: z.number().int().nullable(),
  readyAt: Iso,
  committedAt: Iso.nullable(),
  admittedAt: Iso.nullable(),
  /** Seconds waited for capacity (still waiting: until now). */
  waitedSeconds: z.number().int(),
  deferrals: z.number().int(),
  lastReason: z.string().nullable(),
  callStatus: z.string(),
});
export type PriorityQueueItem = z.infer<typeof PriorityQueueItemSchema>;

export const COMMITMENT_STATES = ['scheduled', 'queued', 'attempted', 'sent', 'cancelled', 'blocked', 'failed'] as const;
export const CommitmentStateSchema = z.enum(COMMITMENT_STATES);
export type CommitmentState = z.infer<typeof CommitmentStateSchema>;
export const COMMITMENT_STATE_LABELS: Record<CommitmentState, string> = {
  scheduled: 'Programada',
  queued: 'En cola',
  attempted: 'Llamada realizada',
  sent: 'WhatsApp enviado',
  cancelled: 'Cancelada (ya no aplica)',
  blocked: 'No se pudo llamar',
  failed: 'Falló',
};

export const CommitmentSchema = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id,
  projectName: z.string().nullable(),
  /** voice = the AI calls at the agreed time; whatsapp = a contextual WhatsApp at the agreed time (§02.j). */
  channel: z.enum(['voice', 'whatsapp']),
  note: z.string().nullable(),
  source: z.string(),
  state: CommitmentStateSchema,
  /** Committed time (agreed with the prospect). */
  committedAt: Iso,
  /** Actual time of the attempt = first submitted call attempt (WhatsApp: when the message was handed to the provider); null while no attempt happened (never invented). */
  actualAt: Iso.nullable(),
  /** actualAt − committedAt in seconds (still pending and overdue: now − committedAt). null when not due yet. */
  delaySeconds: z.number().int().nullable(),
  callId: Id.nullable(),
  reason: z.string().nullable(),
});
export type Commitment = z.infer<typeof CommitmentSchema>;

/** p50/p95 in seconds; null = no samples (unknown ≠ 0). */
export const LatencyStatsSchema = z.object({
  samples: z.number().int(),
  p50Seconds: z.number().nullable(),
  p95Seconds: z.number().nullable(),
});
export type LatencyStats = z.infer<typeof LatencyStatsSchema>;

export const PriorityMetricsSchema = z.object({
  since: Iso,
  /** Form leads (Meta/Google/web form first touch) created in the period: lead → first outbound attempt (call or WhatsApp). */
  firstAttempt: LatencyStatsSchema.extend({
    /** Leads with an attempt within the SLA among those with an attempt; null = no samples. */
    withinSlaRate: z.number().nullable(),
    slaMinutes: z.number().int(),
    /** Form leads with no attempt yet (excluded from the percentiles, never counted as 0). */
    withoutAttempt: z.number().int(),
    /** Leads whose origin is unknown are not form leads for this metric. */
    note: z.string(),
  }),
  /** Calls that waited for capacity: readyAt → admittedAt. */
  queueDelay: LatencyStatsSchema,
  /** Committed-time tasks: committed → actual attempt. */
  commitmentDelay: LatencyStatsSchema,
});
export type PriorityMetrics = z.infer<typeof PriorityMetricsSchema>;

/** Live transfer (spec §05.22) as recorded on a call: what LIA asked the provider and to whom. */
export const LIVE_TRANSFER_STATES = ['requested', 'no_target', 'unsupported', 'failed', 'uncertain', 'answered', 'not_answered'] as const;
export const LiveTransferStateSchema = z.enum(LIVE_TRANSFER_STATES);
export type LiveTransferState = z.infer<typeof LiveTransferStateSchema>;
export const LIVE_TRANSFER_STATE_LABELS: Record<LiveTransferState, string> = {
  requested: 'Transferencia solicitada al proveedor (sin verificar)',
  no_target: 'Ningún asesor disponible: devolución de llamada',
  unsupported: 'El proveedor no permite transferir esta llamada: devolución de llamada',
  failed: 'El proveedor rechazó la transferencia: devolución de llamada',
  uncertain: 'Resultado de la transferencia incierto',
  answered: 'El asesor contestó: atención humana definitiva (sin verificar)',
  not_answered: 'El asesor no contestó: devolución de llamada con un responsable',
};

/** Live transfer of a call (spec §05.22) as shown in the call detail and «Cola de llamadas». Provider side «sin verificar». */
export const LiveTransferViewSchema = z.object({
  state: LiveTransferStateSchema,
  label: z.string(),
  targetName: z.string().nullable(),
  /** When LIA asked the provider. */
  requestedAt: Iso,
  /** The advisor's leg answered (provider webhook); null while unknown — never assumed. */
  answeredAt: Iso.nullable(),
  /** The advisor's leg ended (or was not answered). */
  endedAt: Iso.nullable(),
  detail: z.string().nullable(),
});
export type LiveTransferView = z.infer<typeof LiveTransferViewSchema>;

export const LiveTransferItemSchema = LiveTransferViewSchema.extend({
  callId: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
});
export type LiveTransferItem = z.infer<typeof LiveTransferItemSchema>;

export const PriorityQueueViewSchema = z.object({
  capacity: z.object({
    limit: z.number().int(),
    /** true = server default (no workspace setting). */
    limitIsDefault: z.boolean(),
    activeCalls: z.number().int(),
    waiting: z.number().int(),
    lineLimits: z.array(z.object({ endpointId: Id, name: z.string(), limit: z.number().int(), activeCalls: z.number().int() })),
  }),
  settings: WorkspacePriorityConfigSchema.extend({ defaultMaxConcurrentCalls: z.number().int() }),
  classes: z.array(z.object({ key: PriorityClassSchema, label: z.string(), specRef: z.string() })),
  waiting: z.array(PriorityQueueItemSchema),
  recent: z.array(PriorityQueueItemSchema),
  commitments: z.array(CommitmentSchema),
  metrics: PriorityMetricsSchema,
  lines: z.array(z.object({ endpointId: Id, name: z.string() })),
  /** Setter §05.22: recent live AI→human transfers on calls (requested / answered / not answered). */
  transfers: z.array(LiveTransferItemSchema).default([]),
  /** Honest status of the provider side. */
  verification: z.string(),
});
export type PriorityQueueView = z.infer<typeof PriorityQueueViewSchema>;

export const PriorityQueueQuerySchema = z.object({ days: z.coerce.number().int().min(1).max(90).default(7) });

export const CancelCommitmentSchema = z.object({ reason: z.string().trim().min(3).max(300) }).strict();


